import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from "lucide-react";


const AboutUs = () => {
   const navigate = useNavigate();
  const handleFreeConsultation = () => navigate('/contact');
  return (
    <>
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16">

        {/* LEFT SIDE */}
        <div>
          {/* Header */}
          <p className="text-[#d97925] uppercase tracking-widest font-semibold text-sm mb-3">
            Who We Are
          </p>

          <h2 className="text-5xl font-bold tracking-tight leading-tight mb-6">
            Built to solve India's most complex compliance challenge
          </h2>

          {/* Text */}
          <div className="space-y-5 text-zinc-600 leading-relaxed text-lg">
            <p>
              India's regulatory environment encompasses{" "}
              <strong>over 1,500 central and state laws</strong>, 69,000+
              compliance requirements annually, and constantly evolving
              amendments that every organisation must track and act upon.
            </p>

            <p>
              Most businesses struggle — relying on fragmented, manual processes
              or outdated software. The result is{" "}
              <strong>
                financial penalties, reputational risk, and operational disruption
              </strong>. TickBox was built to solve this.
            </p>

            <p>
              Today, TickBox serves businesses of all sizes — acting not merely
              as a service provider but as a{" "}
              <strong>compliance guardian</strong>: protecting operations,
              reducing liability, and creating conditions for confident, uninterrupted growth.
            </p>
          </div>

          {/* PHASE LIST */}
          <div className="mt-12 space-y-8">
            {[
              {
                num: "1",
                title: "Discover & Audit",
                desc: "Comprehensive compliance audit mapping all applicable laws, obligations, and gaps across your organisation."
              },
              {
                num: "2",
                title: "Design & Onboard",
                desc: "Customised compliance calendar, dedicated account team, and platform integration. Live within 7–14 business days."
              },
              {
                num: "3",
                title: "Execute & Monitor",
                desc: "We manage all filings, renewals, audits, and inspections with real-time dashboard visibility."
              },
              {
                num: "4",
                title: "Review & Optimise",
                desc: "Quarterly reviews to stay ahead of regulatory changes, with continuous workflow and cost optimisation."
              }
            ].map((item, i) => (
              <div key={i} className="flex gap-5 items-start">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d97925] text-white font-bold">
                  {item.num}
                </div>
                <div>
                  <h4 className="text-xl font-semibold">{item.title}</h4>
                  <p className="text-zinc-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div>
          <p className="text-[#d97925] uppercase tracking-widest font-semibold text-sm mb-6">
            Our 6 Core Values
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                icon: "⚖️",
                name: "Integrity",
                desc: "Honesty and ethical practice in every interaction. Clients trust us with sensitive data — we honour that completely."
              },
              {
                icon: "💡",
                name: "Innovation",
                desc: "Continuous R&D building smarter, leaner compliance systems that redefine statutory management."
              },
              {
                icon: "🤝",
                name: "Customer Focus",
                desc: "Every product feature is designed around real client needs. We measure success by the impact we deliver."
              },
              {
                icon: "🏆",
                name: "Excellence",
                desc: "Precision, consistency, and quality in every mandate. Complete confidence in every filing and submission."
              },
              {
                icon: "🎯",
                name: "Specificity",
                desc: "Solutions tailored to the intricate, jurisdiction-specific realities of Indian statutory law."
              },
              {
                icon: "📈",
                name: "Accountability",
                desc: "We take ownership of every commitment — delivering with discipline and full documentation."
              }
            ].map((val, i) => (
              <div
                key={i}
                className="border border-zinc-200 rounded-2xl p-6 hover:shadow-xl transition"
              >
                <div className="text-3xl mb-3">{val.icon}</div>
                <h4 className="text-xl font-semibold mb-2">{val.name}</h4>
                <p className="text-zinc-600 text-sm leading-relaxed">
                  {val.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>

    <section className="py-28 bg-gradient-to-br from-[#111111] to-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-5xl font-bold tracking-tight mb-8"
          >
            Built on Trust. Driven by Care.
          </motion.h2>

          <p className="text-2xl mb-12 max-w-3xl mx-auto opacity-90">
           Committed to delivering clear advice and dependable support.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleFreeConsultation}
            className="inline-flex items-center gap-4 px-12 py-7 bg-white text-[#d97925] font-bold text-2xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-colors"
          >
            Get Started with TickBox
            <ArrowRight size={32} />
          </motion.button>
        </div>
      </section>
      </>
  );
};

export default AboutUs;